import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/pt/categories';

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies: 'ri-t-shirt-line',
  tshirts: 'ri-t-shirt-air-line',
  jackets: 'ri-shirt-line',
  pants: 'ri-pantone-line',
  headwear: 'ri-base-station-line',
  sets: 'ri-stack-line',
  underwear: 'ri-archive-line',
  jersey: 'ri-team-line',
  accessories: 'ri-glasses-line',
  bags: 'ri-briefcase-line',
  belts: 'ri-link-m',
  perfumes: 'ri-contrast-drop-line',
  other: 'ri-more-line',
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/92 backdrop-blur-xl border-b border-background-200/50">
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
              <i className="ri-file-list-3-line text-sm text-white w-4 h-4 flex items-center justify-center"></i>
            </div>
            <span className="text-sm md:text-base font-bold text-foreground-900 whitespace-nowrap">
              cssbuyspreadsheet
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Início
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Categorias</span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-base w-4 h-4 flex items-center justify-center"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-base w-4 h-4 flex items-center justify-center"></i>
                )}
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-white rounded-lg border border-background-200/80 py-2 max-h-[460px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${categoryIcons[cat.id] || 'ri-more-line'} text-xs text-foreground-500 w-4 h-4 flex items-center justify-center`}></i>
                      </div>
                      <span>{cat.namePt}</span>
                      {cat.badge && (
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium whitespace-nowrap">
                          {cat.badge}
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/artigo/guia-cssbuy-spreadsheet"
              className="text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Guia de Compras
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              CSSBuy Sheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 bg-foreground-900 hover:bg-foreground-800 text-white text-sm font-semibold rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line ml-1.5 text-base w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
          >
            <i className={`text-xl w-5 h-5 flex items-center justify-center ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/40 pt-3">
            <Link
              to="/"
              className="flex items-center gap-2 px-2 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200/30"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line text-base w-5 h-5 flex items-center justify-center"></i>
              Início
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-2 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200/30 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-base w-5 h-5 flex items-center justify-center"></i>
                Categorias
              </span>
              {mobileCatOpen ? (
                <i className="ri-arrow-up-s-line text-base w-5 h-5 flex items-center justify-center"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-base w-5 h-5 flex items-center justify-center"></i>
              )}
            </button>

            {mobileCatOpen && (
              <div className="pl-4 space-y-0.5 bg-background-100/50 rounded-lg mx-2 my-2 py-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-700 hover:text-accent-600 transition-colors"
                    onClick={() => { setMobileCatOpen(false); setMobileOpen(false); }}
                  >
                    <div className="w-6 h-6 rounded bg-background-200 flex items-center justify-center flex-shrink-0">
                      <i className={`${categoryIcons[cat.id] || 'ri-more-line'} text-[10px] text-foreground-500 w-3 h-3 flex items-center justify-center`}></i>
                    </div>
                    {cat.namePt}
                    {cat.badge && (
                      <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-accent-100 text-accent-700 rounded-full font-medium whitespace-nowrap">
                        {cat.badge}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/artigo/guia-cssbuy-spreadsheet"
              className="flex items-center gap-2 px-2 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200/30"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
              Guia de Compras
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-2 py-3 text-sm font-semibold text-accent-600 border-b border-background-200/30"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-external-link-line text-base w-5 h-5 flex items-center justify-center"></i>
              CSSBuy Sheet
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mx-2 mt-3 px-4 py-2.5 bg-foreground-900 text-white text-sm font-semibold rounded-md"
              onClick={() => setMobileOpen(false)}
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-base w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}